import { EntityType, type BuildingGraphNode, type EnergyType, type Meter } from "@eisox/api/core-api";
import type { GraphIndex } from "@eisox/tools";

import { EQUIPMENT_TYPES } from "~/constants/building.constant";
import { elementName } from "~/helpers/building.helper";

import { ENERGY_ORDER } from "../consumption.constant";

interface MeterNode {
  meter: Meter;
  depth: number;
}

interface MeterGroup {
  energyType?: EnergyType;
  nodes: MeterNode[];
}

const compareNames = (a: Meter, b: Meter): number => a.name.localeCompare(b.name, undefined, { numeric: true });

const subMetersOf = (meters: Meter[], meterId: string): Meter[] =>
  meters.filter(({ parentId }) => parentId === meterId).sort(compareNames);

const meterGroups = (meters: Meter[], parentsOnly = false): MeterGroup[] => {
  const ids = new Set(meters.map(({ id }) => id));
  const parentIds = new Set(meters.map(({ parentId }) => parentId));
  const mainMeters = meters.filter(({ parentId }) => parentId === undefined || !ids.has(parentId)).sort(compareNames);
  const withSubMeters = (meter: Meter, depth: number): MeterNode[] => [
    { meter, depth },
    ...subMetersOf(meters, meter.id).flatMap(subMeter => withSubMeters(subMeter, depth + 1)),
  ];
  return [...ENERGY_ORDER, undefined]
    .map(energyType => ({
      energyType,
      nodes: mainMeters
        .filter(meter => meter.energyType === energyType)
        .flatMap(meter => withSubMeters(meter, 0))
        .filter(({ meter }) => !parentsOnly || parentIds.has(meter.id)),
    }))
    .filter(({ nodes }) => nodes.length > 0);
};

const firstMainMeter = (meters: Meter[]): Meter | undefined => meterGroups(meters)[0]?.nodes[0]?.meter;

const meterLinks = (graph: GraphIndex<BuildingGraphNode>, meterId: string): string =>
  graph
    .parents(meterId)
    .filter(({ type }) => type !== EntityType.BUILDING && type !== EntityType.METER)
    .map(parent => {
      const equipments = graph
        .ancestors(parent.id)
        .flat()
        .filter(({ type }) => EQUIPMENT_TYPES.has(type))
        .reverse();
      return [...equipments, parent].map(elementName).join(" › ");
    })
    .join(" · ");

export { firstMainMeter, meterGroups, meterLinks, subMetersOf };
